import { Divider } from '@mui/material';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GENERAL_OCCUPANCY_REQUEST,
  PERFOMANCE_HIGHLIGHTS_REQUEST,
  GET_PERFORMANCE_SUMMARY_REQUEST
} from '../../../../redux/actions';
import { generalOccupancySelector } from '../../../../redux/selectors/occupancyRateSelector';
import { perfomanceHighlightsSelector } from '../../../../redux/selectors/perfomanceHighlightsSelector';
import { Highlight } from '../../../common/BasicHighlight/BasicHighlight';
import { OccupancyHighlight } from '../../../common/CircleOccupancyHighlight/CircleOccupancyHighlight';
import classes from './PerfomanceHighlights.module.scss';
import { isNumber } from '../../../../utils/commonUtils';
import {
  L3060D,
  L12M,
  N30D,
  L30D,
} from '../../../../utils/constants/filterConstants';
import { isMobileModeSelector } from '../../../../redux/selectors/appStatusSelector';
import { performancePagePeriodSelector } from '../../../../redux/selectors/performancePageSelector';
import { clsx } from 'clsx';
import { Typography } from '../../../../shared/ui/typography';
import { performanceSummarySelector } from '../../../../redux/selectors/performanceSummarySelector';
import { listingsSelector } from '@/redux/selectors/listingsSelector';
import { IListing } from '@/views/interfaces';
import { useMemo } from 'react';

export const PerfomanceHighlights = () => {
  const dispatch = useDispatch();
  const period = useSelector(performancePagePeriodSelector);

  const summaryData = useSelector(performanceSummarySelector(period));
  const listings: IListing[] = useSelector(listingsSelector);
  const body = useMemo(() => {
    return (listings !== null ? listings : [])
      .filter((listing) => listing.is_listed)
      .map((listing) => listing.property_id);
  }, [listings]);

  // console.log('period test: ', summaryData);

  var all_time_income = '';
  var unique_reservations = '';
  var previous_unique_reservations = '';
  var occupancy = '';
  var previous_occupancy = '';
  var adr = '';
  var previous_adr = '';
  var income = '';
  var previous_income = '';
 if(period == 'last 12 months'){
    all_time_income = summaryData?.summary?.all_time_income;
    unique_reservations = summaryData?.summary?.unique_reservations_L12M;
    previous_unique_reservations = summaryData?.summary?.unique_reservations_L24M_12M;
    occupancy = summaryData?.summary?.occupancy_YTD;
    previous_occupancy = summaryData?.summary?.occupancy_YTD;
    adr = summaryData?.summary?.adr_YTD;
    previous_adr = summaryData?.summary?.adr_YTD;
    income = summaryData?.summary?.income_YTD;
    previous_income = summaryData?.summary?.income_YTD;

  }else if(period == 'next 30 days'){
    all_time_income = summaryData?.summary?.all_time_income;
    unique_reservations = summaryData?.summary?.unique_reservations_N30D;
    previous_unique_reservations = summaryData?.summary?.unique_reservations_L30D;
    occupancy = summaryData?.summary?.occupancy_N30D;
    previous_occupancy = summaryData?.summary?.occupancy_N30D;
    adr = summaryData?.summary?.adr_N30D;
    previous_adr = summaryData?.summary?.adr_N30D;
    income = summaryData?.summary?.income_N30D;
    previous_income = summaryData?.summary?.income_N30D;
  }
  else{
    all_time_income = summaryData?.summary?.all_time_income;
    unique_reservations = summaryData?.summary?.unique_reservations_L30D;
    previous_unique_reservations = summaryData?.summary?.unique_reservations_L30D;
    occupancy = summaryData?.summary?.occupancy_L30D;
    previous_occupancy = summaryData?.summary?.occupancy_L30D;
    adr = summaryData?.summary?.adr_L30D;
    previous_adr = summaryData?.summary?.adr_L30D;
    income = summaryData?.summary?.income_L30D;
    previous_income = summaryData?.summary?.income_L30D;
  }
 
  const isMobileView = useSelector(isMobileModeSelector); 

  useEffect(() => {    
    dispatch({
      type: GET_PERFORMANCE_SUMMARY_REQUEST,
      payload: body,
    });
  }, [dispatch, body]);

  if (!summaryData || Object.keys(summaryData).length === 0 || !summaryData) {
    return (
      <div className={classes.perfomanceHighlightsContainer}>
        <Typography
          variant='text-lg'
          weight='medium'
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.perfomanceHighlightsContainer}>
      <div className={classes.allTimeRevenue}>
        <Highlight
          className={clsx(classes.highlight)}
          label='All Time Revenue'
          value={all_time_income ?? 0}
          previousValue={all_time_income ?? 0}
          currency
          changeValueColor
          showDynamicIndicator={period !== N30D && period !== L30D && period !== L12M}
        />
      </div>
      <div className={classes.perfomanceHighlightsWrapper}>
        <div className={classes.highlights}>
          {isNumber(unique_reservations) && (
            <>
              <Highlight
                className={clsx(classes.highlight)}
                label='Unique Reservations'
                tooltip={
                  'Represents the amount of reservations Checking-in during the selected period'
                }
                value={unique_reservations ?? 0}
                previousValue={previous_unique_reservations ?? 0}
                showDynamicIndicator={period !== N30D && period !== L30D}
              />
              <Divider
                flexItem
                orientation={isMobileView ? 'horizontal' : 'vertical'}
              />
            </>
          )}
          {isNumber(occupancy) && (
            <>
              <Highlight
                className={clsx(classes.highlight)}
                label='Occupied Nights'
                tooltip={
                  'Represents the number of nights that your property was occupied for the given period'
                }
                value={occupancy}
                previousValue={previous_occupancy}
                showDynamicIndicator={period !== N30D && period !== L30D}
              />
              <Divider
                flexItem
                orientation={isMobileView ? 'horizontal' : 'vertical'}
              />
            </>
          )}
          {isNumber(adr) && (
            <>
              <Highlight
                className={clsx(classes.highlight)}
                label='Average Daily Rate'
                currency
                tooltip={
                  'Represents the average daily rate per night for the given period after discounts (e.g. weekly & monthly discounts).'
                }
                value={adr}
                previousValue={previous_adr}
                showDynamicIndicator={period !== N30D && period !== L30D}
              />
              <Divider
                flexItem
                orientation={isMobileView ? 'horizontal' : 'vertical'}
              />
            </>
          )}
          {isNumber(income) && (
            <>
              <Highlight
                className={clsx(classes.highlight)}
                label='Net Reservation Income'
                currency
                tooltip={
                  'This is your net income from secured reservations minus fees. This number is calculated by taking the Net Accommodation Fare subtracting the Channel, Credit Card and Booking Fees.'
                }
                value={income ?? 0}
                previousValue={previous_income ?? 0}
                showDynamicIndicator={period !== N30D && period !== L30D}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
